import { useEffect, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Field } from '../../components/molecules/Field'
import type {
  CreateStoreInput,
  StoreDto,
  UpdateStoreInput
} from '../../../viewmodels/useStores'

const SLUG_MAX = 32
const SLUG_MIN = 2
const DISPLAY_NAME_MAX = 128
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: StoreDto | null
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateStoreInput | UpdateStoreInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

interface FormState {
  slug: string
  display_name: string
}

const EMPTY: FormState = { slug: '', display_name: '' }

function clientValidate(state: FormState): { field: keyof FormState; message: string } | null {
  const slug = state.slug.trim().toLowerCase()
  if (slug.length < SLUG_MIN || slug.length > SLUG_MAX) {
    return { field: 'slug', message: `slug must be ${SLUG_MIN}..${SLUG_MAX} chars` }
  }
  if (!SLUG_PATTERN.test(slug)) {
    return {
      field: 'slug',
      message: 'slug must be lowercase letters / digits / hyphens (e.g. acme-bamboo)'
    }
  }
  const name = state.display_name.trim()
  if (name.length < 1 || name.length > DISPLAY_NAME_MAX) {
    return { field: 'display_name', message: `display_name must be 1..${DISPLAY_NAME_MAX} chars` }
  }
  return null
}

/**
 * StoreFormModal — Add + Edit shared form. Only slug + display_name
 * are operator-editable today; status transitions later via the
 * Publish flow + org verification.
 */
export function StoreFormModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: keyof FormState; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setState(
        initial === null
          ? EMPTY
          : { slug: initial.slug, display_name: initial.display_name }
      )
      setFieldError(null)
      setFormError(null)
      setSubmitting(false)
    }
  }, [open, initial])

  const handleSubmit = async (): Promise<void> => {
    const clientErr = clientValidate(state)
    if (clientErr !== null) {
      setFieldError(clientErr)
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)
    const payload: CreateStoreInput | UpdateStoreInput = {
      slug: state.slug.trim().toLowerCase(),
      display_name: state.display_name.trim()
    }
    const result = await onSubmit(payload)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const f = result.field
    if (f !== null && f !== undefined && f in EMPTY) {
      setFieldError({ field: f as keyof FormState, message: result.message })
    } else {
      setFormError(result.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add store' : `Edit ${initial?.display_name ?? ''}`}
      maxWidth="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

        <Field
          label="Slug (subdomain)"
          helper="Lowercase letters / digits / hyphens. Used as the subdomain piece, e.g. acme.uyyamadilu.com"
          error={fieldError?.field === 'slug' ? fieldError.message : null}
        >
          <Input
            value={state.slug}
            hasError={fieldError?.field === 'slug'}
            onChange={(e) => setState((s) => ({ ...s, slug: e.target.value }))}
            placeholder="e.g. acme-bamboo"
            maxLength={SLUG_MAX}
          />
        </Field>

        <Field
          label="Display name"
          error={fieldError?.field === 'display_name' ? fieldError.message : null}
        >
          <Input
            value={state.display_name}
            hasError={fieldError?.field === 'display_name'}
            onChange={(e) => setState((s) => ({ ...s, display_name: e.target.value }))}
            placeholder="e.g. Acme Bamboo Co."
            maxLength={DISPLAY_NAME_MAX}
          />
        </Field>
      </div>
    </Modal>
  )
}
